import React, {
    ReactElement, useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation } from "react-router";
import { Paths, PathValues } from "./Routing";

const THEME_STORAGE_KEY = "theme";

const WHITELIST_URLS = new Set<string>([
    Paths.AGENT,
    Paths.REPORTS,
    Paths.ACCOUNT_PROFLE,
    Paths.ONBOARDING,
    Paths.ONBOARDING_STEP,
    Paths.HOME,
    Paths.TICKETS,
    Paths.ADMINISTRATOR,
    Paths.ADMINISTRATOR_SETTING,
    Paths.ADMINISTRATOR_SETTING_ID,
    Paths.ANSWERS_GET,
    Paths.ANSWERS_LIST,
    Paths.ANSWERS_NEW,
]);

const DISABLE_LEGACY_URLS = new Set<string>([
    Paths.ONBOARDING,
    Paths.ONBOARDING_STEP,
    Paths.REPORTS,
    Paths.ANSWERS_LIST,
    Paths.ANSWERS_GET,
    Paths.ANSWERS_GET_VERSION,
]);

export const THEME_DATA_KEY = "data-bs-theme";

export enum Themes {
    DEFAULT = "default",
    LIGHT = "light",
    DARK = "dark"
}

export interface ThemeContextAction {
    resetTheme(): void;
    setTheme(theme: Themes): void;
    setV3(): void;
    getTheme(): Themes;
    isV3: boolean;
    isPageV3(page: PathValues): boolean;
    setFeatureFlags(featureFlags: Set<string>): void;
}

export const ThemeContext = React.createContext<ThemeContextAction>({
    resetTheme() {
        // do nothing
    },
    setTheme() {
        // do nothing
    },
    getTheme() : Themes {
        return Themes.DEFAULT;
    },
    setV3(): void {
        // do nothing
    },
    isV3: false,
    isPageV3() {
        return false;
    },
    setFeatureFlags() {
        // do nothing
    },
});

const FEATURE_THEME = "frontend.theme.v3";

const PAGE_FLAGS: Record<string, string> = {
    [Paths.REPORTS]: "frontend.reports.v3",
    [Paths.HOME]: "frontend.pages-v3.home",
    [Paths.ONBOARDING]: "frontend.onboarding.v2",
    [Paths.ONBOARDING_STEP]: "frontend.onboarding.v2",
    [Paths.TICKETS]: "frontend.pages-v3.tickets",
    [Paths.ADMINISTRATOR]: "frontend.pages-v3.administrator",
    [Paths.ADMINISTRATOR_SETTING]: "frontend.pages-v3.administrator",
    [Paths.ADMINISTRATOR_SETTING_ID]: "frontend.pages-v3.administrator",
    [Paths.ANSWERS_GET]: "frontend.pages-v3.answers",
    [Paths.ANSWERS_LIST]: "frontend.pages-v3.answers",
    [Paths.ANSWERS_NEW]: "frontend.pages-v3.answers",

};

function getDefaultV3Theme() {
    let theme = Themes.LIGHT;
    try {
        theme = window.matchMedia("(prefers-color-scheme: dark)").matches ? Themes.DARK : Themes.LIGHT;
    } catch {
        // its ok.
    }

    return theme;
}
function getDefaultTheme(featureFlags: Set<string>) {
    const theme = localStorage.getItem(THEME_STORAGE_KEY) as Themes;
    if (theme) {
        return theme;
    }

    if (!featureFlags.has(FEATURE_THEME)) {
        return Themes.DEFAULT;
    }
    return getDefaultV3Theme();
}
export function ThemeProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [currentTheme, setTheme] = useState(() => getDefaultTheme(new Set()));
    const [flags, setFlags] = useState<Set<string>>();
    const isPageFlagEnabled = (page: PathValues) => {
        const flagName = page ? PAGE_FLAGS[page] : undefined;
        return (flagName && flags) ? flags.has(flagName) : (page != null);
    };
    const getPageTheme = (page: PathValues) => {
        const isLegacyDisabled = DISABLE_LEGACY_URLS.has(page);
        if (isLegacyDisabled) {
            return currentTheme === Themes.DEFAULT ? getDefaultV3Theme() : currentTheme;
        }

        return isPageFlagEnabled(page as PathValues) ? currentTheme : Themes.DEFAULT;
    };

    const getCurrentTheme = () => {
        const topLevelPath = `/${window.location.pathname.split("/")[1]}`;
        const page = WHITELIST_URLS.has(topLevelPath);
        return page ? getPageTheme(topLevelPath as PathValues) : Themes.DEFAULT;
    };

    const instance = useMemo<ThemeContextAction>(() => ({
        resetTheme() {
            setTheme(getDefaultV3Theme());
            localStorage.removeItem(THEME_STORAGE_KEY);
        },
        setTheme(theme: Themes) {
            setTheme(theme ?? Themes.DEFAULT);
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        },
        setV3() {
            setTheme(getDefaultV3Theme());
        },
        getTheme() {
            return getCurrentTheme();
        },
        get isV3() {
            const isV3 = getCurrentTheme() !== Themes.DEFAULT;
            return isV3;
        },
        isPageV3(page: PathValues) {
            return getPageTheme(page) !== Themes.DEFAULT;
        },
        setFeatureFlags(featureFlags: Set<string>) {
            setFlags(featureFlags);
            setTheme(getDefaultTheme(featureFlags));
        },
    }), [currentTheme, flags]);

    const location = useLocation();

    useEffect(() => {
        const theme = getCurrentTheme();
        const body = document.getElementsByTagName("body").item(0);
        body?.setAttribute(THEME_DATA_KEY, theme);
    }, [flags, currentTheme, location.pathname]);

    return (
        <ThemeContext.Provider value={instance}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme(): ThemeContextAction {
    return useContext(ThemeContext) ?? {};
}
